<script setup>
import { ref, watch, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { dynamicTime } from 'shared/helpers/timeHelper';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';

import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  contactId: {
    type: [String, Number],
    required: true,
  },
});

const { t } = useI18n();
const store = useStore();
const { formatMessage } = useMessageFormatter();

// State
const newNoteContent = ref('');
const editingNoteId = ref(null);
const editingContent = ref('');

// Getters
const currentUser = useMapGetter('getCurrentUser');
const uiFlags = useMapGetter('contactNotes/getUIFlags');
const notesGetterFn = useMapGetter('contactNotes/getAllNotesByContactId');

// Computed
const isFetchingNotes = computed(() => uiFlags.value.isFetching);
const isCreatingNote = computed(() => uiFlags.value.isCreating);
const isUpdatingNote = computed(() => uiFlags.value.isUpdating);
const notes = computed(() => notesGetterFn.value(props.contactId));

const getWrittenBy = ({ user } = {}) => {
  const currentUserId = currentUser.value?.id;
  return user?.id === currentUserId
    ? t('CONTACTS_LAYOUT.SIDEBAR.NOTES.YOU')
    : user?.name || t('CONVERSATION.BOT');
};

const isOwnNote = note => {
  return note?.user?.id === currentUser.value?.id;
};

// Actions
const onCreateNote = async () => {
  if (!newNoteContent.value.trim()) return;

  try {
    await store.dispatch('contactNotes/create', {
      contactId: props.contactId,
      content: newNoteContent.value,
    });
    newNoteContent.value = '';
    useAlert(t('CONTACTS_LAYOUT.SIDEBAR.NOTES.NOTE_ADDED'));
  } catch (error) {
    useAlert(t('CONTACTS_LAYOUT.SIDEBAR.NOTES.ERROR_ADDING_NOTE'));
  }
};

const onStartEdit = note => {
  editingNoteId.value = note.id;
  editingContent.value = note.content;
};

const onCancelEdit = () => {
  editingNoteId.value = null;
  editingContent.value = '';
};

const onSaveEdit = async noteId => {
  if (!editingContent.value.trim()) return;

  try {
    await store.dispatch('contactNotes/update', {
      contactId: props.contactId,
      noteId,
      content: editingContent.value,
    });
    editingNoteId.value = null;
    editingContent.value = '';
    useAlert(t('CONTACTS_LAYOUT.SIDEBAR.NOTES.NOTE_UPDATED'));
  } catch (error) {
    useAlert(t('CONTACTS_LAYOUT.SIDEBAR.NOTES.ERROR_UPDATING_NOTE'));
  }
};

const onDeleteNote = async noteId => {
  try {
    await store.dispatch('contactNotes/delete', {
      contactId: props.contactId,
      noteId,
    });
    useAlert(t('CONTACTS_LAYOUT.SIDEBAR.NOTES.NOTE_DELETED'));
  } catch (error) {
    useAlert(t('CONTACTS_LAYOUT.SIDEBAR.NOTES.ERROR_DELETING_NOTE'));
  }
};

// Fetch notes when contactId changes
watch(
  () => props.contactId,
  () => store.dispatch('contactNotes/get', { contactId: props.contactId }),
  { immediate: true }
);
</script>

<template>
  <div class="flex flex-col h-full">
    <!-- Create New Note Section -->
    <div class="p-3 border-b border-n-weak">
      <div class="flex flex-col gap-2">
        <textarea
          v-model="newNoteContent"
          :placeholder="t('CONTACTS_LAYOUT.SIDEBAR.NOTES.PLACEHOLDER')"
          class="w-full p-3 text-sm border rounded-lg resize-none bg-n-alpha-1 border-n-weak text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:ring-2 focus:ring-n-brand focus:border-transparent"
          rows="2"
          @keydown.ctrl.enter="onCreateNote"
          @keydown.meta.enter="onCreateNote"
        />
        <div class="flex justify-end">
          <Button
            size="sm"
            color="blue"
            :label="t('CONTACTS_LAYOUT.SIDEBAR.NOTES.SAVE')"
            :is-loading="isCreatingNote"
            :disabled="!newNoteContent.trim() || isCreatingNote"
            @click="onCreateNote"
          />
        </div>
      </div>
    </div>

    <!-- Notes List -->
    <div class="flex-1 overflow-y-auto">
      <!-- Loading State -->
      <div v-if="isFetchingNotes" class="p-8 grid place-content-center">
        <Spinner />
      </div>

      <!-- Empty State -->
      <div
        v-else-if="!notes.length"
        class="p-8 grid place-content-center text-center"
      >
        <p class="text-sm text-n-slate-11">
          {{ t('CONTACTS_LAYOUT.SIDEBAR.NOTES.NO_NOTES') }}
        </p>
      </div>

      <!-- Notes List -->
      <div v-else class="divide-y divide-n-weak">
        <div
          v-for="note in notes"
          :key="note.id"
          class="p-3 group hover:bg-n-alpha-1 transition-colors"
        >
          <!-- Note Header -->
          <div class="flex items-center justify-between gap-2 mb-2">
            <div class="flex items-center gap-1.5 min-w-0">
              <Avatar
                :name="note?.user?.name || 'Bot'"
                :src="
                  note?.user?.name
                    ? note?.user?.thumbnail
                    : '/assets/images/chatwoot_bot.png'
                "
                :size="20"
                rounded-full
              />
              <span class="text-xs text-n-slate-11 truncate">
                <span class="font-medium text-n-slate-12">
                  {{ getWrittenBy(note) }}
                </span>
                · {{ dynamicTime(note.createdAt) }}
              </span>
            </div>

            <!-- Action Buttons (visible on hover or when editing) -->
            <div
              v-if="editingNoteId !== note.id"
              class="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <Button
                v-if="isOwnNote(note)"
                v-tooltip.top="t('CONTACTS_LAYOUT.SIDEBAR.NOTES.EDIT')"
                ghost
                slate
                xs
                icon="i-lucide-pencil"
                @click="onStartEdit(note)"
              />
              <Button
                v-tooltip.top="t('CONTACTS_LAYOUT.SIDEBAR.NOTES.DELETE')"
                ghost
                color="ruby"
                xs
                icon="i-lucide-trash-2"
                @click="onDeleteNote(note.id)"
              />
            </div>
          </div>

          <!-- Note Content (View Mode) -->
          <div v-if="editingNoteId !== note.id">
            <p
              v-dompurify-html="formatMessage(note.content || '')"
              class="text-sm text-n-slate-12 prose-sm prose-p:text-sm prose-p:leading-relaxed prose-p:mb-1 prose-p:mt-0"
            />
          </div>

          <!-- Note Content (Edit Mode) -->
          <div v-else class="flex flex-col gap-2">
            <textarea
              v-model="editingContent"
              class="w-full p-2 text-sm border rounded-lg resize-none bg-n-alpha-1 border-n-weak text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand focus:border-transparent"
              rows="3"
              @keydown.escape="onCancelEdit"
              @keydown.ctrl.enter="onSaveEdit(note.id)"
              @keydown.meta.enter="onSaveEdit(note.id)"
            />
            <div class="flex justify-end gap-2">
              <Button
                size="xs"
                ghost
                slate
                :label="t('CONTACTS_LAYOUT.SIDEBAR.NOTES.CANCEL')"
                @click="onCancelEdit"
              />
              <Button
                size="xs"
                color="blue"
                :label="t('CONTACTS_LAYOUT.SIDEBAR.NOTES.SAVE')"
                :is-loading="isUpdatingNote"
                :disabled="!editingContent.trim() || isUpdatingNote"
                @click="onSaveEdit(note.id)"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
